import SupportAgentRounded from '@mui/icons-material/SupportAgentRounded';
import { Box, Card, Link, Stack, Tooltip, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { api, unwrap } from '../../api/client';
import type { PlatformSupportGrant } from '../../api/m19';
import { DataTable, type Column } from '../../components/DataTable';
import { EmptyState, ErrorState, PageHeader } from '../../components/states';
import { StatusChip } from '../../components/StatusChip';
import { Segmented } from '../../components/ui';
import { dateTime, plural, relativeTime } from '../../lib/format';

type Status = 'active' | 'all';

/** Shops that currently let platform support look at their data (read-only, time-limited). */
export function SupportGrantsPage() {
  const [params, setParams] = useSearchParams();
  const status: Status = params.get('status') === 'all' ? 'all' : 'active';
  const query = useQuery({
    queryKey: ['support-grants', status],
    queryFn: () => unwrap(api.GET('/platform/support-grants', { params: { query: { status } } })),
  });
  const rows = query.data?.data;

  const columns: Column<PlatformSupportGrant>[] = [
    {
      key: 'tenant',
      header: 'Shop',
      render: (g) => (
        <Box sx={{ minWidth: 0 }}>
          <Link
            component={RouterLink}
            to={`/tenants/${g.tenant.id}`}
            underline="hover"
            sx={{ fontWeight: 600 }}
          >
            {g.tenant.name}
          </Link>
          <Typography variant="caption" color="text.secondary" component="div">
            {g.tenant.slug}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'grantedBy',
      header: 'Granted by',
      hideBelow: 'sm',
      render: (g) => (
        <Box>
          <Typography variant="body2">{g.grantedBy.name}</Typography>
          <Tooltip title={dateTime(g.createdAt)}>
            <Typography variant="caption" color="text.secondary">
              {relativeTime(g.createdAt)}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      key: 'note',
      header: 'Note',
      hideBelow: 'md',
      render: (g) =>
        g.note ? (
          <Typography variant="body2" sx={{ maxWidth: 320, overflowWrap: 'anywhere' }}>
            {g.note}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        ),
    },
    {
      key: 'expires',
      header: 'Expires',
      render: (g) => (
        <Tooltip title={dateTime(g.expiresAt)}>
          <Typography
            variant="body2"
            className="tabular"
            color={g.status === 'ACTIVE' ? 'text.primary' : 'text.secondary'}
            sx={{ whiteSpace: 'nowrap', fontWeight: g.status === 'ACTIVE' ? 600 : 400 }}
          >
            {relativeTime(g.expiresAt)}
          </Typography>
        </Tooltip>
      ),
    },
    { key: 'status', header: 'Status', render: (g) => <StatusChip status={g.status} /> },
    {
      key: 'revokedBy',
      header: 'Revoked',
      hideBelow: 'lg',
      render: (g) =>
        g.revokedAt ? (
          <Box>
            <Typography variant="body2">
              {g.revokedBy?.name ?? 'Replaced by a new grant'}
            </Typography>
            <Tooltip title={dateTime(g.revokedAt)}>
              <Typography variant="caption" color="text.secondary">
                {relativeTime(g.revokedAt)}
              </Typography>
            </Tooltip>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        ),
    },
  ];

  return (
    <Box>
      <PageHeader
        eyebrow="Customers"
        title="Support access"
        subtitle="Shops grant read-only access from their settings for 1–72 hours. Every support read is audited in both logs."
      />
      {query.isError ? (
        <Card>
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </Card>
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(g) => g.id}
          loading={query.isPending}
          toolbar={
            <Stack
              direction="row"
              sx={{ alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}
            >
              <Segmented<Status>
                label="Grant status"
                value={status}
                onChange={(v) => {
                  const next = new URLSearchParams(params);
                  if (v === 'all') next.set('status', 'all');
                  else next.delete('status');
                  setParams(next, { replace: true });
                }}
                options={[
                  { value: 'active', label: 'Active now' },
                  { value: 'all', label: 'All grants' },
                ]}
              />
              {rows && (
                <Typography variant="body2" color="text.secondary">
                  {plural(rows.length, 'grant')}
                </Typography>
              )}
            </Stack>
          }
          empty={
            <EmptyState
              dense
              icon={<SupportAgentRounded />}
              title={status === 'active' ? 'No shop is sharing access now' : 'No grants yet'}
              body={
                status === 'active'
                  ? 'When a shop owner turns on support access in their settings, it shows up here until it expires.'
                  : 'No shop has granted support access yet.'
              }
            />
          }
        />
      )}
    </Box>
  );
}
